// src/layouts/AppLayout.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Outlet, Navigate } from "react-router-dom";
import TopBar from "../components/TopBar";
import { useAuth } from "./auth/AuthContext";
import { getAllowedBranches } from "./auth/allowedBranches";
import api from "../axiosConfig";
import { Modal, Button, Form } from "react-bootstrap";

export default function AppLayout() {
  const { user, logout, isAuthed, activeBranchId, setActiveBranchId } = useAuth();
  const [trialExpired, setTrialExpired] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(null);

  const [branchesList, setBranchesList] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [selectedBranchVal, setSelectedBranchVal] = useState("");

  useEffect(() => {
    if (!isAuthed || !user) return;
    setLoadingBranches(true);
    api.get("/branches/?limit=1000")
      .then((res) => {
        if (res.data && res.data.items) {
          setBranchesList(res.data.items);
        }
      })
      .catch((err) => {
        console.error("Failed to load branches:", err);
      })
      .finally(() => {
        setLoadingBranches(false);
      });
  }, [isAuthed, user]);

  const allowedBranches = useMemo(() => {
    return getAllowedBranches(user, branchesList);
  }, [branchesList, user]);

  useEffect(() => {
    if (loadingBranches || !allowedBranches.length) return;
    
    // Auto-select if only 1 branch is available
    if (allowedBranches.length === 1) {
      const singleId = allowedBranches[0].id;
      if (activeBranchId !== singleId) {
        setActiveBranchId(singleId);
      }
    }
  }, [loadingBranches, allowedBranches, activeBranchId, setActiveBranchId]);

  useEffect(() => {
    if (!isAuthed || !user) return;
    
    // Fetch company settings to verify trial status
    api.get("/firma")
      .then((res) => {
        if (res.data && res.data.items && res.data.items.length > 0) {
          const firma = res.data.items[0];
          if (firma.data_instalarii && !firma.ok_go) {
            const installDate = new Date(firma.data_instalarii);
            installDate.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const diffTime = today - installDate;
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            
            if (diffDays > 30) {
              setTrialExpired(true);
            } else {
              setDaysRemaining(30 - diffDays);
            }
          } else {
            // Reset state if ok_go is true or data_instalarii is missing
            setTrialExpired(false);
            setDaysRemaining(null);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to load company config for trial check:", err);
      });
  }, [isAuthed, user]);

  if (!isAuthed) return <Navigate to="/login" replace />;

  const hasAllBranches = user?.user_name === "superadmin" || user?.perms?.includes("ALL_BRANCHES");

  const showModal = isAuthed && !loadingBranches && allowedBranches.length > 1 && !hasAllBranches && (
    !activeBranchId || !allowedBranches.some(b => b.id === activeBranchId)
  );

  const handleSelectBranchSubmit = (e) => {
    e.preventDefault();
    if (selectedBranchVal) {
      setActiveBranchId(Number(selectedBranchVal));
    }
  };

  return (
    <div className="app-layout position-relative min-vh-100">
      <TopBar user={user} onLogout={logout} />
      <div className="pt-2" />
      {trialExpired && !user?.is_superuser ? (
        <div className="container mt-5">
          <div className="alert alert-danger shadow p-5 text-center" role="alert">
            <h2 className="alert-heading fw-bold mb-4">Perioada de Probă a Expirat</h2>
            <p className="fs-5 mb-4">
              Perioada de probă de 30 de zile pentru această firmă a expirat. Accesul la date și operațiuni a fost suspendat.
            </p>
            <hr />
            <p className="mb-0 mt-4 text-muted fs-6">
              Vă rugăm să contactați administratorul sistemului pentru a valida continuarea utilizării.
            </p>
          </div>
        </div>
      ) : (
        <>
          {daysRemaining !== null && daysRemaining <= 5 && !user?.is_superuser && (
            <div className="alert alert-warning text-center mb-0 py-2 border-0 rounded-0" role="alert">
              Atenție: Au mai rămas {daysRemaining} zile din perioada de probă de 30 de zile.
            </div>
          )}
          <Outlet />
        </>
      )}

      {/* Branch Selection Modal */}
      <Modal show={showModal} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title className="fw-bold">Selectare Punct de Lucru</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-4">
            Contul tău are acces la mai multe puncte de lucru (branches). Selectează punctul de lucru în care dorești să lucrezi:
          </p>
          <Form onSubmit={handleSelectBranchSubmit}>
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">Punct de lucru activ</Form.Label>
              <Form.Select
                value={selectedBranchVal}
                onChange={(e) => setSelectedBranchVal(e.target.value)}
                required
              >
                <option value="">-- Alege un punct de lucru --</option>
                {allowedBranches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.code} — {b.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <div className="d-flex gap-2 justify-content-end mt-4">
              <Button variant="outline-danger" onClick={logout}>
                Deconectare
              </Button>
              <Button type="submit" variant="primary" disabled={!selectedBranchVal} className="px-4 fw-semibold">
                Confirmă
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
